import { addToast, ErrorToast } from '@otter-ai/den-components';
import { updateCheckoutSession } from '@otter-ai/recall-client';
import {
  CheckoutSessionActionType,
  PurchaseCheckoutSessionUpdateEvent,
  PurchaseSeatSelectionEvent,
  trackEvent,
} from '@otter-ai/telemetry';
import { useCheckout } from '@stripe/react-stripe-js';
import { useMutation } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';
import { useEffect } from 'react';
import { seatCountIsUpdatingAtom } from '#atoms';
import { useSeatCount } from './useSeatCount.ts';

export function useUpdateSeatCount({
  setNumSeats,
}: {
  setNumSeats: (seatCount: number) => void;
}) {
  const { runServerUpdate, id, lineItems } = useCheckout();
  const setSeatCountIsUpdating = useSetAtom(seatCountIsUpdatingAtom);
  const { validatedSeatCount, minSeatCount } = useSeatCount();

  const updateSeatCountMutation = useMutation<number, Error, string, void>({
    mutationFn: async (value: string) => {
      let seatCountToUpdate = Number(value);
      if (!validatedSeatCount({ value })) {
        const errorToast = (
          <ErrorToast description={`${minSeatCount} minimum seats`} />
        );
        addToast(errorToast);
        seatCountToUpdate = minSeatCount;
      }

      setNumSeats(seatCountToUpdate);

      const res = await runServerUpdate(() =>
        updateCheckoutSession({
          body: {
            checkout_session_id: id,
            session_params: {
              line_items: [
                {
                  id: lineItems[0]?.id ?? '',
                  quantity: seatCountToUpdate,
                },
              ],
            },
          },
          throwOnError: true,
        }),
      );
      if (res.type === 'success') {
        const seatCount = res.session.lineItems[0]?.quantity ?? 0;
        return seatCount;
      }
      return seatCountToUpdate;
    },
    onSuccess: (seatCount: number) => {
      trackEvent(
        PurchaseCheckoutSessionUpdateEvent({
          Status: 'success',
          CheckoutSessionActionType: CheckoutSessionActionType.ChangeSeat,
          SeatCount: seatCount,
        }),
      );
      setNumSeats(seatCount);
    },
    onError: (error, value) => {
      trackEvent(
        PurchaseCheckoutSessionUpdateEvent({
          Status: 'failure',
          ErrorMessage: error.message,
          CheckoutSessionActionType: CheckoutSessionActionType.ChangeSeat,
          SeatCount: Number(value),
        }),
      );
      setNumSeats(lineItems[0]?.quantity ?? 0);
    },
    onSettled: (seatCount, _, variables) => {
      trackEvent(
        PurchaseSeatSelectionEvent({
          SeatCount: seatCount ?? Number(variables),
        }),
      );
    },
  });

  useEffect(() => {
    setSeatCountIsUpdating(updateSeatCountMutation.isPending);
  }, [updateSeatCountMutation.isPending, setSeatCountIsUpdating]);

  return updateSeatCountMutation;
}
